import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IntegrationVendor } from '../../models/integration/integration-vendor.enum';
import { VENDOR_DEFINITIONS } from '../../models/integration/vendor-definitions.constant';
import { IntegrationProvider } from '../../provider/integration.provider';
import { SetupStep } from '../../reducer/integration.reducer';

/**
 * Modal step shown to the user. Local view-state derived from `pendingSetup.step`.
 * NOTE: no `created` step: the popup already tells the user the connection succeeded,
 * so we close this modal immediately and let the mapping modal take over.
 */
export type ModalViewStep = 'welcome' | 'awaiting_consent' | 'error';

@Component({
    selector: 'aumico-d365bc-connect-modal',
    templateUrl: './d365bc-connect-modal.component.html',
    styleUrls: ['./d365bc-connect-modal.component.scss'],
})
export class D365BcConnectModalComponent implements OnInit, OnDestroy {
    @Output() public closed = new EventEmitter<void>();

    public step: ModalViewStep = 'welcome';
    public errorMessage: string | null = null;
    public tenantIdentifier: string | null = null;

    /** Form values: local to the modal until submitted with the consent request. */
    public environmentName = 'production';
    public customDisplayName = '';

    public readonly vendorDefinition = VENDOR_DEFINITIONS.find(
        (v) => v.vendor === IntegrationVendor.D365_BC,
    );

    private readonly destroy$ = new Subject<void>();

    public constructor(private readonly integrationProvider: IntegrationProvider) {}

    public ngOnInit(): void {
        this.integrationProvider
            .getPendingSetup$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((setup) => {
                this.errorMessage = setup.error;
                this.tenantIdentifier = setup.tenantIdentifier;

                if (setup.vendor !== IntegrationVendor.D365_BC) return;

                // On success: the popup already confirmed the connection to the user.
                // Close this modal immediately so the mapping modal can take over.
                if (setup.step === 'created') {
                    this.closed.emit();
                    return;
                }

                this.step = this.viewStepFor(setup.step);
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Both fields required. Display name is what distinguishes tenants in the
     * manage modal's tenant list — defaulting it to the env name (which is
     * often just "production" across multiple tenants) would make the list
     * unreadable. We enforce a non empty value in the UI rather than silently
     * defaulting.
     */
    public get isFormValid(): boolean {
        return this.environmentName.trim().length > 0 && this.customDisplayName.trim().length > 0;
    }

    public onStartConsent(): void {
        if (!this.isFormValid) return;
        const env = this.environmentName.trim();
        const displayName = this.customDisplayName.trim();
        this.integrationProvider.startConnect(IntegrationVendor.D365_BC, env, displayName);
    }

    public onRetryFromError(): void {
        this.errorMessage = null;
        this.step = 'welcome';
    }

    public onCancel(): void {
        this.integrationProvider.cancelSetup();
        this.closed.emit();
    }

    private viewStepFor(reducerStep: SetupStep): ModalViewStep {
        switch (reducerStep) {
            case 'awaiting_consent':
                return 'awaiting_consent';
            case 'error':
                return 'error';
            case 'created':
                // Handled separately by closing the modal: should never be rendered.
                return 'welcome';
            case 'idle':
            default:
                return 'welcome';
        }
    }
}
